import { Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import {
  AI,
  GenerativeModel,
  GenerateContentRequest,
  GoogleAIBackend,
  ModelParams,
  getAI,
  getGenerativeModel,
} from '@firebase/ai';
import { environment } from '../../environments/environment';

const BATCH_SIZE = 50;

@Injectable({ providedIn: 'root' })
export class LyricsService {
  private aiInstance?: AI;
  private model?: GenerativeModel;

  constructor(private firebaseApp: FirebaseApp) {}

  /**
   * Translates an array of caption lines via Gemini in batches of 50.
   * Returns a parallel array of the same length — missing entries are empty strings.
   */
  async translateLines(lines: string[], fromLang: string, toLang: string): Promise<string[]> {
    if (!lines.length) return [];

    const results: string[] = [];

    for (let i = 0; i < lines.length; i += BATCH_SIZE) {
      const batch = lines.slice(i, i + BATCH_SIZE);
      const translated = await this.translateBatch(batch, fromLang, toLang);
      results.push(...translated);
    }

    // Ensure the output length always matches the input length
    while (results.length < lines.length) results.push('');
    results.length = lines.length;

    return results;
  }

  private async translateBatch(lines: string[], fromLang: string, toLang: string): Promise<string[]> {
    const prompt =
      `You are a translator. Translate each of the following "${fromLang}" caption lines to "${toLang}". ` +
      `Return ONLY a JSON array of strings with exactly ${lines.length} elements in the same order. ` +
      `Keep each translation short — one line per input line, no explanations.\n` +
      `Input: ${JSON.stringify(lines)}`;

    const request: GenerateContentRequest = {
      contents: [{ role: 'user', parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.1,
        maxOutputTokens: 2048,
        topP: 0.9,
        responseMimeType: 'application/json',
      },
    };

    const model = this.getModel();
    const response = await model.generateContent(request);
    const candidates = response.response.candidates;
    if (!candidates?.length) throw new Error('No AI response');

    const text = candidates[0].content.parts[0].text;
    if (!text) throw new Error('Empty AI response');

    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) throw new Error('Invalid response format');

    // Pad or trim to match input batch size
    const out = parsed as string[];
    while (out.length < lines.length) out.push('');
    out.length = lines.length;

    return out;
  }

  private getModel(): GenerativeModel {
    if (this.model) return this.model;
    const ai = this.getAiInstance();
    const params: ModelParams = { model: environment.generativeAI.model };
    this.model = getGenerativeModel(ai, params);
    return this.model;
  }

  private getAiInstance(): AI {
    if (this.aiInstance) return this.aiInstance;
    this.aiInstance = getAI(this.firebaseApp, { backend: new GoogleAIBackend() });
    return this.aiInstance;
  }
}
